// pages/Designations.jsx — Simple CRUD for job titles
import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { usePermission, Can } from '../lib/permissions';
import Portal from '../components/Portal';
import { Plus, X, Briefcase, Trash2, Edit } from 'lucide-react';

export default function Designations() {
  const { has } = usePermission();
  const canManage = has('designations.manage');
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get('/settings/designations');
    setItems(data.designations);
  };
  useEffect(() => { load(); }, []);

  const del = async (d) => {
    if (!confirm(`Delete "${d.name}"?`)) return;
    await api.delete(`/settings/designations/${d.id}`);
    load();
  };

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="designations-page">
      <div className="flex items-center justify-between mb-6" style={{ color: 'var(--trello-text)' }}>
        <div>
          <div className="flex items-center gap-2"><Briefcase size={22} /><h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Designations</h1></div>
          <p style={{ opacity: 0.85, marginTop: 4, fontSize: 13 }}>Job titles that can be assigned to users.</p>
        </div>
        <Can perm="designations.manage">
          <button className="tm-btn" onClick={() => setEditing('new')} data-testid="new-desig-btn" style={{ padding: '8px 16px', borderRadius: 10 }}>
            <Plus size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />New designation
          </button>
        </Can>
      </div>

      <div className="tm-stat-card" style={{ borderLeft: 'none' }} data-testid="desig-list">
        {items.length === 0 && <p style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No designations yet.</p>}
        {items.map((d) => (
          <div key={d.id} className="flex items-center gap-3 p-3" style={{ borderBottom: '1px solid var(--trello-border)' }} data-testid={`desig-${d.id}`}>
            <Briefcase size={16} color="var(--trello-text-muted)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{d.name}</div>
              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{d.user_count || 0} users</div>
            </div>
            {canManage && (
              <>
                <button className="tm-btn-ghost" onClick={() => setEditing(d)} data-testid={`edit-desig-${d.id}`}><Edit size={14} /></button>
                <button className="tm-btn-ghost" onClick={() => del(d)} data-testid={`del-desig-${d.id}`} style={{ color: '#DE350B' }}><Trash2 size={14} /></button>
              </>
            )}
          </div>
        ))}
      </div>

      {editing && <DesigModal desig={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function DesigModal({ desig, onClose, onSaved }) {
  const isNew = !desig;
  const [name, setName] = useState(desig?.name || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return alert('Name required');
    setBusy(true);
    try {
      if (isNew) await api.post('/settings/designations', { name: name.trim() });
      else await api.patch(`/settings/designations/${desig.id}`, { name: name.trim() });
      onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Portal>
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()} data-testid="desig-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{isNew ? 'New designation' : `Edit — ${desig.name}`}</h2>
          <button className="tm-btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="tm-label">Name</div>
        <input className="tm-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Senior Engineer" autoFocus data-testid="desig-name-input"
          onKeyDown={(e) => e.key === 'Enter' && save()} />
        <div className="flex justify-end gap-2 mt-4">
          <button className="tm-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="tm-btn" onClick={save} disabled={busy} data-testid="save-desig">{busy ? 'Saving…' : (isNew ? 'Create' : 'Save')}</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
